import { runCopyToWorkspaceWorkflow } from "@/action-dialog";
import type { FilesRailFileMenuActions } from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/files/FilesRailFileRow";
import { useFileRecordCardActions } from "@/pages/workspaces/hooks/useFileRecordCardActions";
import type { FileRecord } from "@cohesive/api-client";
import { useCallback, useMemo } from "react";

type UseFilesRailContextFileActionsParams = {
  allRecords: FileRecord[];
  /** When set, placed canvas files have their node removed before delete. */
  fileRecordIdToNodeId?: Map<string, string>;
  /**
   * Flow-session files always copy (never move) into a workspace — matches
   * cross-class transfer rules and keeps the chat attachment intact.
   */
  copyToWorkspaceInsteadOfMove?: boolean;
};

/** Menu actions + dialogs for canvas / flow-session file rows in the Files rail context section. */
export function useFilesRailContextFileActions({
  allRecords,
  fileRecordIdToNodeId,
  copyToWorkspaceInsteadOfMove = false,
}: UseFilesRailContextFileActionsParams) {
  const { dialogs: baseDialogs, actions } = useFileRecordCardActions({
    allRecords,
    onFolderOpen: () => {},
  });

  const handleDelete = useCallback(
    (record: FileRecord) => {
      const nodeId = fileRecordIdToNodeId?.get(record.id);
      if (nodeId) {
        window.dispatchEvent(
          new CustomEvent("canvas:remove-node", {
            detail: { nodeId, origin: "no-undo:file-delete" },
          }),
        );
      }
      actions.delete(record);
    },
    [actions.delete, fileRecordIdToNodeId],
  );

  const handleCopyToWorkspace = useCallback((record: FileRecord) => {
    window.setTimeout(() => void runCopyToWorkspaceWorkflow({ record }), 0);
  }, []);

  const menuActions = useMemo((): FilesRailFileMenuActions => {
    const shared = {
      onRename: actions.rename,
      onDelete: handleDelete,
    };
    if (copyToWorkspaceInsteadOfMove) {
      return { ...shared, onCopy: handleCopyToWorkspace };
    }
    return {
      ...shared,
      onCopy: actions.copy,
      onMove: actions.move,
    };
  }, [
    actions.rename,
    actions.copy,
    actions.move,
    handleDelete,
    handleCopyToWorkspace,
    copyToWorkspaceInsteadOfMove,
  ]);

  return { menuActions, dialogs: baseDialogs };
}
